import Head from 'next/head'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Turn {
  id: string
  user_message: string
  agent_response: string
  upvotes: number
  downvotes: number
  score: number
  created_at: string
}

interface Conversation {
  conversation_id: string
  started_at: string
  total_upvotes: number
  total_downvotes: number
  total_score: number
  turns: Turn[]
}

// ── VoteButton ────────────────────────────────────────────────────────────────

function VoteButton({ rowId, initialUpvotes, initialDownvotes, initialScore }: {
  rowId: string
  initialUpvotes: number
  initialDownvotes: number
  initialScore: number
}) {
  const [upvotes, setUpvotes] = useState(initialUpvotes)
  const [downvotes, setDownvotes] = useState(initialDownvotes)
  const [score, setScore] = useState(initialScore)
  const [voted, setVoted] = useState<1 | -1 | null>(null)
  const [loading, setLoading] = useState(false)

  const handleVote = async (vote: 1 | -1) => {
    if (loading || voted !== null) return
    setLoading(true)
    if (vote === 1) setUpvotes((v) => v + 1)
    else setDownvotes((v) => v + 1)
    setScore((s) => s + vote)
    setVoted(vote)

    try {
      const res = await fetch('/api/ai-vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ row_id: rowId, vote }),
      })
      if (!res.ok) {
        if (vote === 1) setUpvotes((v) => v - 1)
        else setDownvotes((v) => v - 1)
        setScore((s) => s - vote)
        setVoted(null)
      }
    } catch {
      if (vote === 1) setUpvotes((v) => v - 1)
      else setDownvotes((v) => v - 1)
      setScore((s) => s - vote)
      setVoted(null)
    } finally {
      setLoading(false)
    }
  }

  const scoreColor = score > 0 ? '#26834B' : score < 0 ? '#DF1615' : '#939598'

  return (
    <div className='flex items-center gap-2 mt-2'>
      <button
        type='button'
        onClick={() => handleVote(1)}
        disabled={voted !== null || loading}
        className={`flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          voted === 1
            ? 'border-parks-green bg-light-green text-dark-green'
            : 'border-light-dark-gray text-50-grey hover:border-parks-green hover:text-parks-green'
        }`}
      >
        ▲ {upvotes}
      </button>
      <button
        type='button'
        onClick={() => handleVote(-1)}
        disabled={voted !== null || loading}
        className={`flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          voted === -1
            ? 'border-red bg-red-100 text-red-700'
            : 'border-light-dark-gray text-50-grey hover:border-red hover:text-red'
        }`}
      >
        ▼ {downvotes}
      </button>
      <span className='text-base font-medium' style={{ color: scoreColor }}>
        {score > 0 ? `+${score}` : score}
      </span>
      {voted !== null && <span className='text-base italic text-50-grey'>voted</span>}
    </div>
  )
}

// ── ConversationCard ──────────────────────────────────────────────────────────

function ConversationCard({ conv }: { conv: Conversation }) {
  const [expanded, setExpanded] = useState(false)

  const shortId = conv.conversation_id.slice(0, 8)
  const date = new Date(conv.started_at).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  const preview = conv.turns[0]?.user_message ?? '—'
  const previewText = preview.length > 80 ? `${preview.slice(0, 80)}...` : preview
  const scoreColor =
    conv.total_score > 0 ? 'text-parks-green' : conv.total_score < 0 ? 'text-red' : 'text-50-grey'

  return (
    <div className='overflow-hidden rounded-xl border border-light-dark-gray bg-white'>
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='flex w-full items-start justify-between gap-4 px-5 py-4 text-left hover:bg-lightest-gray transition-colors'
      >
        <div className='min-w-0 flex-1'>
          <div className='mb-1 flex items-center gap-2'>
            <span className='font-mono text-base text-50-grey'>#{shortId}</span>
            <span className='text-base text-50-grey'>·</span>
            <span className='text-base text-50-grey'>{date}</span>
            <span className='text-base text-50-grey'>·</span>
            <span className='text-base text-50-grey'>
              {conv.turns.length} turn{conv.turns.length !== 1 ? 's' : ''}
            </span>
          </div>
          <p className='truncate text-base text-black'>{previewText}</p>
        </div>
        <div className='flex flex-shrink-0 items-center gap-3'>
          <div className='text-right'>
            <p className={`text-base font-medium ${scoreColor}`}>
              {conv.total_score > 0 ? `+${conv.total_score}` : conv.total_score}
            </p>
            <p className='text-base text-50-grey'>score</p>
          </div>
          <span className='text-light-dark-gray'>{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div className='divide-y divide-light-dark-gray border-t border-light-dark-gray'>
          {conv.turns.map((turn, i) => (
            <div key={turn.id} className='px-5 py-4'>
              <div className='mb-3 flex items-center gap-2'>
                <span className='text-base text-50-grey'>Turn {i + 1}</span>
              </div>
              <div className='mb-3'>
                <p className='mb-1 text-base font-medium text-medium-dark-gray'>User</p>
                <p className='text-lg leading-relaxed text-black'>{turn.user_message}</p>
              </div>
              <div>
                <p className='mb-1 text-base font-medium text-bright-templi-purple'>Agent</p>
                <div className='text-lg leading-relaxed text-black'>
                  <ReactMarkdown>{turn.agent_response}</ReactMarkdown>
                </div>
                <VoteButton
                  rowId={turn.id}
                  initialUpvotes={turn.upvotes}
                  initialDownvotes={turn.downvotes}
                  initialScore={turn.score}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AiDashboard() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'top' | 'bottom'>('all')

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch('/api/ai-conversations')
        if (!res.ok) throw new Error('Failed to load')
        const data = await res.json()
        setConversations(
          (data.conversations ?? []).map((c: Conversation) => ({
            ...c,
            total_score: c.total_score ?? 0,
            total_upvotes: c.total_upvotes ?? 0,
            total_downvotes: c.total_downvotes ?? 0,
            turns: Array.isArray(c.turns) ? c.turns : [],
          })),
        )
      } catch {
        setError('Could not load conversations.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [])

  const filtered = conversations
    .filter((c) => {
      if (filter === 'top') return c.total_score >= 0
      if (filter === 'bottom') return c.total_score <= 0
      return true
    })
    .sort((a, b) => {
      if (filter === 'top') return b.total_score - a.total_score
      if (filter === 'bottom') return a.total_score - b.total_score
      return new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
    })

  return (
    <>
      <Head>
        <title>AI Conversations Dashboard — Templi</title>
      </Head>

      <div className='min-h-screen bg-pure-white font-roboto'>
        {/* Header */}
        <div className='border-b border-light-dark-gray bg-white px-6 py-5'>
          <div className='mx-auto flex max-w-4xl items-center justify-between'>
            <div>
              <h1 className='text-2xl font-bold text-black'>AI Conversations</h1>
              <p className='mt-0.5 text-base text-medium-dark-gray'>
                Vote on agent responses to improve future answers
              </p>
            </div>
            <a
              href='/'
              className='text-base text-medium-dark-gray hover:text-bright-templi-purple transition-colors'
            >
              ← Back to site
            </a>
          </div>
        </div>

        {/* Filters */}
        <div className='border-b border-light-dark-gray bg-white px-6 py-3'>
          <div className='mx-auto flex max-w-4xl items-center gap-2'>
            {(['all', 'top', 'bottom'] as const).map((f) => (
              <button
                key={f}
                type='button'
                onClick={() => setFilter(f)}
                className={`rounded-full border px-3 py-1.5 text-sm capitalize transition-colors ${
                  filter === f
                    ? 'border-bright-templi-purple bg-bright-templi-purple text-white'
                    : 'border-light-dark-gray text-medium-dark-gray hover:border-medium-dark-gray hover:text-black'
                }`}
              >
                {f === 'all' ? 'All' : f === 'top' ? '▲ Top rated' : '▼ Low rated'}
              </button>
            ))}
            <span className='ml-auto text-base text-medium-dark-gray'>
              {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className='mx-auto max-w-4xl px-6 py-6'>
          {loading && (
            <div className='flex items-center justify-center py-24'>
              <p className='text-base text-medium-dark-gray'>Loading conversations...</p>
            </div>
          )}
          {error && (
            <div className='flex items-center justify-center py-24'>
              <p className='text-base text-red'>{error}</p>
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className='flex items-center justify-center py-24'>
              <p className='text-base text-medium-dark-gray'>No conversations found.</p>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className='flex flex-col gap-3'>
              {filtered.map((conv) => (
                <ConversationCard key={conv.conversation_id} conv={conv} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}